import { library } from '@fortawesome/fontawesome-svg-core'
import * as fad from '@fortawesome/pro-duotone-svg-icons'
import * as fal from '@fortawesome/pro-light-svg-icons'
import * as far from '@fortawesome/pro-regular-svg-icons'
import * as fas from '@fortawesome/pro-solid-svg-icons'

enum AcceptedTransform {
  DOWN = 'down',
  FLIP_H = 'flipHorizontal',
  FLIP_V = 'flipVertical',
  GROW = 'grow',
  LEFT = 'left',
  RIGHT = 'right',
  ROTATE = 'rotate',
  SHRINK = 'shrink',
  UP = 'up',
}

export interface Transform {
  down?: number
  flipHorizontal?: boolean
  flipVertical?: boolean
  grow?: number
  left?: number
  right?: number
  rotate?: number
  shrink?: number
  up?: number
}

export const iconSizes = [
  'lg',
  'xs',
  'sm',
  '1x',
  '2x',
  '3x',
  '4x',
  '5x',
  '6x',
  '7x',
  '8x',
  '9x',
  '10x',
] as const
export type IconSize = (typeof iconSizes)[number]

export const usedIcons = [
  'address-book',
  'apple-alt',
  'arrow-down',
  'arrow-left',
  'arrow-right-from-bracket',
  'arrow-right',
  'arrow-up',
  'arrow-up-arrow-down',
  'badge-percent',
  'ban',
  'bars',
  'basket-shopping',
  'bell',
  'book',
  'book-open',
  'box-alt',
  'box-taped',
  'boxes-stacked',
  'buildings',
  'calculator',
  'calendar-alt',
  'calendar-clock',
  'calendar-range',
  'calendar',
  'can-food',
  'caret-circle-down',
  'caret-down',
  'caret-left',
  'caret-up',
  'cart-circle-plus',
  'cart-plus',
  'cart-shopping',
  'chart-column',
  'chart-line',
  'chart-mixed-up-circle-dollar',
  'chart-pie',
  'check-circle',
  'check',
  'cheese-swiss',
  'chevron-down',
  'chevron-left',
  'chevron-right',
  'chevron-up',
  'circle-user',
  'circle',
  'circle-xmark',
  'clock',
  'coffee',
  'comment-lines',
  'comment-plus',
  'comment',
  'comments',
  'compress',
  'cookie-bite',
  'copy',
  'croissant',
  'cup-straw',
  'cup-straw-swoosh',
  'dolly',
  'download',
  'edit',
  'ellipsis-v',
  'envelope',
  'exclamation-circle',
  'exclamation-triangle',
  'expand',
  'eye-slash',
  'eye',
  'file-alt',
  'file-csv',
  'file-export',
  'file-exclamation',
  'file-import',
  'file-pdf',
  'file-xls',
  'fish',
  'floppy-disk',
  'floppy-disks',
  'gavel',
  'gears',
  'gift',
  'grip-vertical',
  'handshake',
  'heart',
  'history',
  'hourglass-clock',
  'hourglass-end',
  'hourglass-half',
  'image',
  'info-circle',
  'key',
  'kitchen-set',
  'knife',
  'language',
  'laptop-house',
  'layer-group',
  'lemon',
  'level-down',
  'link-slash',
  'link',
  'list-check',
  'lock',
  'meat',
  'medal',
  'megaphone',
  'meh',
  'message-dollar',
  'microchip-ai',
  'minus',
  'mobile-android-alt',
  'paper-plane',
  'paperclip',
  'paw',
  'pen-to-square',
  'pepper-hot',
  'phone-alt',
  'phone-office',
  'phone-square-alt',
  'plate-utensils',
  'plus',
  'print-slash',
  'print',
  'question-circle',
  'receipt',
  'redo',
  'ranking-star',
  'rotate',
  'rotate-left',
  'ruler',
  'ruler-combined',
  'save',
  'scale-balanced',
  'search',
  'share-square',
  'shop',
  'shopping-basket',
  'shopping-cart',
  'slash',
  'sliders',
  'snowflake',
  'sparkles',
  'star',
  'steak',
  'table',
  'tag',
  'telescope',
  'thumbs-up',
  'thumbtack',
  'times-circle',
  'times',
  'toggle-off',
  'toggle-on',
  'tomato',
  'trash-alt',
  'truck',
  'truck-arrow-right',
  'truck-fast',
  'truck-clock',
  'up-right-from-square',
  'upload',
  'user-pen',
  'user-tag',
  'user-xmark',
  'user-check',
  'user',
  'users',
  'xmark',
  'wand-magic-sparkles',
  'weight-hanging',
] as const

type ElementType<T extends ReadonlyArray<unknown>> =
  T extends ReadonlyArray<infer ElementType> ? ElementType : never

export type UsedIcons = ElementType<typeof usedIcons>

export const formatTransform = (transform?: Transform) => {
  if (!transform) return undefined

  return Object.keys(transform)
    .map(key => {
      switch (key) {
        case AcceptedTransform.FLIP_H:
          return `flip-h`
        case AcceptedTransform.FLIP_V:
          return `flip-v`

        default:
          return `${key}-${transform[key as keyof Transform]}`
      }
    })
    .join(' ')
}

export const addIcons = () =>
  library.add(
    // fad
    fad.faAddressBook,
    fad.faAppleAlt,
    fad.faArrowDown,
    fad.faArrowLeft,
    fad.faArrowRight,
    fad.faArrowRightFromBracket,
    fad.faArrowUp,
    fad.faArrowUpArrowDown,
    fad.faBadgePercent,
    fad.faBan,
    fad.faBars,
    fad.faBasketShopping,
    fad.faBell,
    fad.faBook,
    fad.faBookOpen,
    fad.faBoxAlt,
    fad.faBoxTaped,
    fad.faBoxesStacked,
    fad.faBuildings,
    fad.faCalculator,
    fad.faCalendar,
    fad.faCalendarAlt,
    fad.faCalendarClock,
    fad.faCalendarRange,
    fad.faCanFood,
    fad.faCaretCircleDown,
    fad.faCaretDown,
    fad.faCaretLeft,
    fad.faCaretUp,
    fad.faCartCirclePlus,
    fad.faCartPlus,
    fad.faCartShopping,
    fad.faChartColumn,
    fad.faChartLine,
    fad.faChartMixedUpCircleDollar,
    fad.faChartPie,
    fad.faCheck,
    fad.faCheckCircle,
    fad.faCheeseSwiss,
    fad.faChevronDown,
    fad.faChevronLeft,
    fad.faChevronRight,
    fad.faChevronUp,
    fad.faCircle,
    fad.faCircleUser,
    fad.faCircleXmark,
    fad.faClock,
    fad.faCoffee,
    fad.faComment,
    fad.faCommentLines,
    fad.faCommentPlus,
    fad.faComments,
    fad.faCompress,
    fad.faCookieBite,
    fad.faCopy,
    fad.faCroissant,
    fad.faCupStraw,
    fad.faCupStrawSwoosh,
    fad.faDolly,
    fad.faDownload,
    fad.faEdit,
    fad.faEllipsisV,
    fad.faEnvelope,
    fad.faExclamationCircle,
    fad.faExclamationTriangle,
    fad.faExpand,
    fad.faEye,
    fad.faEyeSlash,
    fad.faFileAlt,
    fad.faFileCsv,
    fad.faFileExport,
    fad.faFileExclamation,
    fad.faFileImport,
    fad.faFilePdf,
    fad.faFileXls,
    fad.faFish,
    fad.faFloppyDisk,
    fad.faFloppyDisks,
    fad.faGavel,
    fad.faGears,
    fad.faGift,
    fad.faGripVertical,
    fad.faHandshake,
    fad.faHeart,
    fad.faHistory,
    fad.faHourglassClock,
    fad.faHourglassEnd,
    fad.faHourglassHalf,
    fad.faImage,
    fad.faInfoCircle,
    fad.faKey,
    fad.faKitchenSet,
    fad.faKnife,
    fad.faLanguage,
    fad.faLaptopHouse,
    fad.faLayerGroup,
    fad.faLemon,
    fad.faLevelDown,
    fad.faLink,
    fad.faLinkSlash,
    fad.faListCheck,
    fad.faLock,
    fad.faMeat,
    fad.faMedal,
    fad.faMegaphone,
    fad.faMeh,
    fad.faMessageDollar,
    fad.faMicrochipAi,
    fad.faMinus,
    fad.faMobileAndroidAlt,
    fad.faPaperclip,
    fad.faPaperPlane,
    fad.faPaw,
    fad.faPenToSquare,
    fad.faPepperHot,
    fad.faPhoneAlt,
    fad.faPhoneOffice,
    fad.faPhoneSquareAlt,
    fad.faPlateUtensils,
    fad.faPlus,
    fad.faPrint,
    fad.faPrintSlash,
    fad.faQuestionCircle,
    fad.faReceipt,
    fad.faRedo,
    fad.faRankingStar,
    fad.faRotate,
    fad.faRotateLeft,
    fad.faRuler,
    fad.faRulerCombined,
    fad.faSave,
    fad.faScaleBalanced,
    fad.faSearch,
    fad.faShareSquare,
    fad.faShoppingCart,
    fad.faShoppingBasket,
    fad.faSlash,
    fad.faSliders,
    fad.faShop,
    fad.faSnowflake,
    fad.faSparkles,
    fad.faStar,
    fad.faSteak,
    fad.faTable,
    fad.faTag,
    fad.faTelescope,
    fad.faThumbsUp,
    fad.faThumbtack,
    fad.faTimes,
    fad.faTimesCircle,
    fad.faToggleOff,
    fad.faToggleOn,
    fad.faTomato,
    fad.faTrashAlt,
    fad.faTruck,
    fad.faTruckArrowRight,
    fad.faTruckClock,
    fad.faTruckFast,
    fad.faUpRightFromSquare,
    fad.faUpload,
    fad.faUser,
    fad.faUserPen,
    fad.faUserXmark,
    fad.faUserCheck,
    fad.faUsers,
    fad.faUserTag,
    fad.faXmark,
    fad.faWandMagicSparkles,
    fad.faWeightHanging,
    // fal
    fal.faAddressBook,
    fal.faAppleAlt,
    fal.faArrowDown,
    fal.faArrowLeft,
    fal.faArrowRight,
    fal.faArrowRightFromBracket,
    fal.faArrowUp,
    fal.faArrowUpArrowDown,
    fal.faBadgePercent,
    fal.faBan,
    fal.faBars,
    fal.faBasketShopping,
    fal.faBell,
    fal.faBook,
    fal.faBookOpen,
    fal.faBoxAlt,
    fal.faBoxTaped,
    fal.faBoxesStacked,
    fal.faBuildings,
    fal.faCalculator,
    fal.faCalendar,
    fal.faCalendarAlt,
    fal.faCalendarClock,
    fal.faCalendarRange,
    fal.faCanFood,
    fal.faCaretCircleDown,
    fal.faCaretDown,
    fal.faCaretLeft,
    fal.faCaretUp,
    fal.faCartCirclePlus,
    fal.faCartPlus,
    fal.faCartShopping,
    fal.faChartColumn,
    fal.faChartLine,
    fal.faChartMixedUpCircleDollar,
    fal.faChartPie,
    fal.faCheck,
    fal.faCheckCircle,
    fal.faCheeseSwiss,
    fal.faChevronDown,
    fal.faChevronLeft,
    fal.faChevronRight,
    fal.faChevronUp,
    fal.faCircle,
    fal.faCircleUser,
    fal.faCircleXmark,
    fal.faClock,
    fal.faCoffee,
    fal.faComment,
    fal.faCommentLines,
    fal.faCommentPlus,
    fal.faComments,
    fal.faCompress,
    fal.faCookieBite,
    fal.faCopy,
    fal.faCroissant,
    fal.faCupStraw,
    fal.faCupStrawSwoosh,
    fal.faDolly,
    fal.faDownload,
    fal.faEdit,
    fal.faEllipsisV,
    fal.faEnvelope,
    fal.faExclamationCircle,
    fal.faExclamationTriangle,
    fal.faExpand,
    fal.faEye,
    fal.faEyeSlash,
    fal.faFileAlt,
    fal.faFileCsv,
    fal.faFileExport,
    fal.faFileExclamation,
    fal.faFileImport,
    fal.faFilePdf,
    fal.faFileXls,
    fal.faFish,
    fal.faFloppyDisk,
    fal.faFloppyDisks,
    fal.faGavel,
    fal.faGears,
    fal.faGift,
    fal.faGripVertical,
    fal.faHandshake,
    fal.faHeart,
    fal.faHistory,
    fal.faHourglassClock,
    fal.faHourglassEnd,
    fal.faHourglassHalf,
    fal.faImage,
    fal.faInfoCircle,
    fal.faKey,
    fal.faKitchenSet,
    fal.faKnife,
    fal.faLanguage,
    fal.faLaptopHouse,
    fal.faLayerGroup,
    fal.faLemon,
    fal.faLevelDown,
    fal.faLink,
    fal.faLinkSlash,
    fal.faListCheck,
    fal.faLock,
    fal.faMeat,
    fal.faMedal,
    fal.faMegaphone,
    fal.faMeh,
    fal.faMessageDollar,
    fal.faMicrochipAi,
    fal.faMinus,
    fal.faMobileAndroidAlt,
    fal.faPaperclip,
    fal.faPaperPlane,
    fal.faPaw,
    fal.faPenToSquare,
    fal.faPepperHot,
    fal.faPhoneAlt,
    fal.faPhoneOffice,
    fal.faPhoneSquareAlt,
    fal.faPlateUtensils,
    fal.faPlus,
    fal.faPrint,
    fal.faPrintSlash,
    fal.faQuestionCircle,
    fal.faReceipt,
    fal.faRedo,
    fal.faRankingStar,
    fal.faRotate,
    fal.faRotateLeft,
    fal.faRuler,
    fal.faRulerCombined,
    fal.faSave,
    fal.faScaleBalanced,
    fal.faShareSquare,
    fal.faSearch,
    fal.faShoppingCart,
    fal.faShoppingBasket,
    fal.faSlash,
    fal.faSliders,
    fal.faShop,
    fal.faSnowflake,
    fal.faSparkles,
    fal.faStar,
    fal.faSteak,
    fal.faTable,
    fal.faTag,
    fal.faTelescope,
    fal.faThumbsUp,
    fal.faThumbTack,
    fal.faTimes,
    fal.faTimesCircle,
    fal.faToggleOff,
    fal.faToggleOn,
    fal.faTomato,
    fal.faTrashAlt,
    fal.faTruck,
    fal.faTruckArrowRight,
    fal.faTruckClock,
    fal.faTruckFast,
    fal.faUpRightFromSquare,
    fal.faUpload,
    fal.faUser,
    fal.faUsers,
    fal.faUserPen,
    fal.faUserXmark,
    fal.faUserCheck,
    fal.faUserTag,
    fal.faXmark,
    fal.faWandMagicSparkles,
    fal.faWeightHanging,
    // far
    far.faAddressBook,
    far.faAppleAlt,
    far.faArrowDown,
    far.faArrowLeft,
    far.faArrowRight,
    far.faArrowRightFromBracket,
    far.faArrowUp,
    far.faArrowUpArrowDown,
    far.faBadgePercent,
    far.faBan,
    far.faBars,
    far.faBasketShopping,
    far.faBell,
    far.faBook,
    far.faBookOpen,
    far.faBoxAlt,
    far.faBoxTaped,
    far.faBoxesStacked,
    far.faBuildings,
    far.faCalculator,
    far.faCalendar,
    far.faCalendarAlt,
    far.faCalendarClock,
    far.faCalendarRange,
    far.faCanFood,
    far.faCaretCircleDown,
    far.faCaretDown,
    far.faCaretLeft,
    far.faCaretUp,
    far.faCartCirclePlus,
    far.faCartPlus,
    far.faCartShopping,
    far.faChartMixedUpCircleDollar,
    far.faChartColumn,
    far.faChartLine,
    far.faChartPie,
    far.faCheck,
    far.faCheckCircle,
    far.faCheeseSwiss,
    far.faChevronDown,
    far.faChevronLeft,
    far.faChevronRight,
    far.faChevronUp,
    far.faCircle,
    far.faCircleUser,
    far.faCircleXmark,
    far.faClock,
    far.faCoffee,
    far.faComment,
    far.faCommentLines,
    far.faCommentPlus,
    far.faComments,
    far.faCompress,
    far.faCookieBite,
    far.faCopy,
    far.faCroissant,
    far.faCupStraw,
    far.faCupStrawSwoosh,
    far.faDolly,
    far.faDownload,
    far.faEdit,
    far.faEllipsisV,
    far.faEnvelope,
    far.faExclamationCircle,
    far.faExclamationTriangle,
    far.faExpand,
    far.faEye,
    far.faEyeSlash,
    far.faFileAlt,
    far.faFileCsv,
    far.faFileExport,
    far.faFileExclamation,
    far.faFileImport,
    far.faFilePdf,
    far.faFileXls,
    far.faFish,
    far.faFloppyDisk,
    far.faFloppyDisks,
    far.faGavel,
    far.faGears,
    far.faGift,
    far.faGripVertical,
    far.faHandshake,
    far.faHeart,
    far.faHistory,
    far.faHourglassClock,
    far.faHourglassEnd,
    far.faHourglassHalf,
    far.faImage,
    far.faInfoCircle,
    far.faKey,
    far.faKitchenSet,
    far.faKnife,
    far.faLanguage,
    far.faLaptopHouse,
    far.faLayerGroup,
    far.faLemon,
    far.faLevelDown,
    far.faLink,
    far.faLinkSlash,
    far.faListCheck,
    far.faLock,
    far.faMeat,
    far.faMedal,
    far.faMegaphone,
    far.faMeh,
    far.faMessageDollar,
    far.faMicrochipAi,
    far.faMinus,
    far.faMobileAndroidAlt,
    far.faPaperclip,
    far.faPaperPlane,
    far.faPaw,
    far.faPenToSquare,
    far.faPepperHot,
    far.faPhoneAlt,
    far.faPhoneOffice,
    far.faPhoneSquareAlt,
    far.faPlateUtensils,
    far.faPlus,
    far.faPrint,
    far.faPrintSlash,
    far.faQuestionCircle,
    far.faReceipt,
    far.faRedo,
    far.faRankingStar,
    far.faRotate,
    far.faRotateLeft,
    far.faRuler,
    far.faRulerCombined,
    far.faShareSquare,
    far.faSave,
    far.faScaleBalanced,
    far.faSearch,
    far.faShoppingCart,
    far.faShoppingBasket,
    far.faSlash,
    far.faSliders,
    far.faShop,
    far.faSnowflake,
    far.faSparkles,
    far.faStar,
    far.faSteak,
    far.faTable,
    far.faTag,
    far.faTelescope,
    far.faThumbsUp,
    far.faThumbTack,
    far.faTimes,
    far.faTimesCircle,
    far.faToggleOff,
    far.faToggleOn,
    far.faTomato,
    far.faTrashAlt,
    far.faTruck,
    far.faTruckArrowRight,
    far.faTruckClock,
    far.faTruckFast,
    far.faUpRightFromSquare,
    far.faUpload,
    far.faUser,
    far.faUserPen,
    far.faUserXmark,
    far.faUserCheck,
    far.faUsers,
    far.faUserTag,
    far.faXmark,
    far.faWandMagicSparkles,
    far.faWeightHanging,
    // fas
    fas.faAddressBook,
    fas.faAppleAlt,
    fas.faArrowDown,
    fas.faArrowLeft,
    fas.faArrowRight,
    fas.faArrowUp,
    fas.faArrowRightFromBracket,
    fas.faArrowUpArrowDown,
    fas.faBadgePercent,
    fas.faBan,
    fas.faBars,
    fas.faBasketShopping,
    fas.faBell,
    fas.faBook,
    fas.faBookOpen,
    fas.faBoxAlt,
    fas.faBoxTaped,
    fas.faBoxesStacked,
    fas.faBuildings,
    fas.faCalculator,
    fas.faCalendar,
    fas.faCalendarAlt,
    fas.faCalendarClock,
    fas.faCalendarRange,
    fas.faCanFood,
    fas.faCaretCircleDown,
    fas.faCaretDown,
    fas.faCaretLeft,
    fas.faCaretUp,
    fas.faCartCirclePlus,
    fas.faCartPlus,
    fas.faCartShopping,
    fas.faChartColumn,
    fas.faChartLine,
    fas.faChartMixedUpCircleDollar,
    fas.faChartPie,
    fas.faCheck,
    fas.faCheckCircle,
    fas.faCheeseSwiss,
    fas.faChevronDown,
    fas.faChevronLeft,
    fas.faChevronRight,
    fas.faChevronUp,
    fas.faCircle,
    fas.faCircleUser,
    fas.faCircleXmark,
    fas.faClock,
    fas.faCoffee,
    fas.faComment,
    fas.faCommentLines,
    fas.faCommentPlus,
    fas.faComments,
    fas.faCompress,
    fas.faCookieBite,
    fas.faCopy,
    fas.faCroissant,
    fas.faCupStraw,
    fas.faCupStrawSwoosh,
    fas.faDolly,
    fas.faDownload,
    fas.faEdit,
    fas.faEllipsisV,
    fas.faEnvelope,
    fas.faExclamationCircle,
    fas.faExclamationTriangle,
    fas.faExpand,
    fas.faEye,
    fas.faEyeSlash,
    fas.faFileAlt,
    fas.faFileCsv,
    fas.faFileExport,
    fas.faFileExclamation,
    fas.faFish,
    fas.faFloppyDisk,
    fas.faFloppyDisks,
    fas.faFileImport,
    fas.faFilePdf,
    fas.faFileXls,
    fas.faGavel,
    fas.faGears,
    fas.faGift,
    fas.faGripVertical,
    fas.faHandshake,
    fas.faHeart,
    fas.faHistory,
    fas.faHourglassClock,
    fas.faHourglassEnd,
    fas.faHourglassHalf,
    fas.faImage,
    fas.faInfoCircle,
    fas.faKey,
    fas.faKitchenSet,
    fas.faKnife,
    fas.faLanguage,
    fas.faLaptopHouse,
    fas.faLayerGroup,
    fas.faLemon,
    fas.faLevelDown,
    fas.faLink,
    fas.faLinkSlash,
    fas.faListCheck,
    fas.faLock,
    fas.faMeat,
    fas.faMedal,
    fas.faMegaphone,
    fas.faMeh,
    fas.faMessageDollar,
    fas.faMicrochipAi,
    fas.faMinus,
    fas.faMobileAndroidAlt,
    fas.faPaperclip,
    fas.faPaperPlane,
    fas.faPaw,
    fas.faPenToSquare,
    fas.faPepperHot,
    fas.faPhoneAlt,
    fas.faPhoneOffice,
    fas.faPhoneSquareAlt,
    fas.faPlateUtensils,
    fas.faPlus,
    fas.faPrint,
    fas.faPrintSlash,
    fas.faQuestionCircle,
    fas.faReceipt,
    fas.faRedo,
    fas.faRankingStar,
    fas.faRotate,
    fas.faRotateLeft,
    fas.faRuler,
    fas.faRulerCombined,
    fas.faShareSquare,
    fas.faSave,
    fas.faScaleBalanced,
    fas.faSearch,
    fas.faShoppingCart,
    fas.faShoppingBasket,
    fas.faSlash,
    fas.faSliders,
    fas.faShop,
    fas.faSnowflake,
    fas.faSparkles,
    fas.faStar,
    fas.faSteak,
    fas.faTable,
    fas.faTag,
    fas.faTelescope,
    fas.faThumbsUp,
    fas.faThumbTack,
    fas.faTimes,
    fas.faTimesCircle,
    fas.faToggleOff,
    fas.faToggleOn,
    fas.faTomato,
    fas.faTrashAlt,
    fas.faTruck,
    fas.faTruckArrowRight,
    fas.faTruckClock,
    fas.faTruckFast,
    fas.faUpRightFromSquare,
    fas.faUpload,
    fas.faUser,
    fas.faUserPen,
    fas.faUserXmark,
    fas.faUserCheck,
    fas.faUsers,
    fas.faUserTag,
    fas.faXmark,
    fas.faWandMagicSparkles,
    fas.faWeightHanging
  )
